import {Hotel} from '@findhotel/sapi'
import {categoryIcons} from 'components/Accommodation/Facilities/CategoryIcon'
import {facilityIconsById} from 'components/Accommodation/Facilities/FacilityIcon'
import {Lov} from 'modules/search/slice'
import {SeoAttributes} from 'seo/seo-db/attributes'
import {getParentCategoryId} from 'utils/facilities'

import {IconName} from '@vio/atlas/src/components/designSystem/Icon'
import {capitalizeFirstLetter} from '@vio/core/src/utils/string'

const MAX_TOP_ATTRIBUTES = 4

const ThemeIconsMap: Partial<Record<number, IconName>> = {
  8: 'Sparkles2',
  3: 'Dollar',
  1: 'Buildings',
  21: 'SpaWellness',
  100: 'Heart'
}

interface TopAttribute {
  icon: IconName
  label: string
}

export const getHotelTopAttributes = (
  seoActiveAttributes: SeoAttributes | undefined,
  hotel: Pick<Hotel, 'facilities' | 'themeIds' | 'starRating' | 'guestRating'>,
  listOfValues: Lov
) => {
  if (!hotel || !listOfValues || !seoActiveAttributes) return []

  const attributes: TopAttribute[] = []
  const attributesSet = new Set<string>() // To track unique attributes

  const rankedAttributes = Object.entries(seoActiveAttributes).sort(
    (a, b) => a[1].priority - b[1].priority
  )

  for (const [, config] of rankedAttributes) {
    // Stop processing if we have reached the maximum number of top attributes
    if (attributes.length >= MAX_TOP_ATTRIBUTES) break

    const {key, value} = config

    // Since some attributes are grouped by the with the same id but different key, we need to check if we already added an attribute with the same value to avoid duplicates
    if (attributesSet.has(value)) continue
    attributesSet.add(value)

    if (key === 'facilities' && hotel.facilities?.includes(Number(value))) {
      const facility = listOfValues[`Facility:${value}`]
      if (!facility) continue

      const icon =
        facilityIconsById[value] ||
        categoryIcons[getParentCategoryId(facility)] ||
        categoryIcons.general

      attributes.push({
        icon,
        label: facility.value as string
      })
      continue
    }

    if (key === 'themeIds' && hotel.themeIds?.includes(Number(value))) {
      attributes.push({
        icon: ThemeIconsMap[Number(config.value)],
        label: capitalizeFirstLetter(config.label)
      })
      continue
    }

    if (key === 'guestRating' && hotel?.guestRating?.overall >= Number(value)) {
      attributes.push({
        icon: 'People',
        label: capitalizeFirstLetter(config.label)
      })
      continue
    }

    if (key === 'starRating' && hotel?.starRating >= Number(value)) {
      attributes.push({
        icon: 'Star',
        label: capitalizeFirstLetter(config.label)
      })
      continue
    }
  }
  return attributes
}
